// Recrutement v2 — sections de l'espace candidat : appel en 7 temps
// (script SHIFTIN v1.0, contrôles de clôture calculés), cas pratique
// rémunéré, vérification avant l'offre, portes de l'offre + dérogation.
// Les sections appellent directement leurs mutations (toast d'erreur
// commun) — elles ne passent pas par les props de la page.
import { useEffect, useState } from 'react';
import {
  REC_REF_QUESTIONS, REC_SETTINGS_DEFAULT, recDimScore, recNeedsEvidence,
  type RecGridCrit, type RecRef,
} from '@pointage/shared';
import { toast } from '../../store/toast.js';
import {
  useMe, useRecBarOverride, useRecCallClose, useRecCallPlan, useRecCallReport, useRecCallSave, useRecInterviewers, useRecSettings, useRecTestAdvance,
  useRecTestPatch, useRecTestPay, useRecTestScore, useRecTestSend, useRecTestSubmit, useRecVerifPatch,
  useRecVerifRef,
  type RecApplicationDTO, type RecDispoDelay, type RecRequisitionDTO, type RecRoleDTO,
} from '../../api/hooks.js';
import { recFmtMoney } from './common.js';
import { RecModalHost } from './CandidateModals.js';
import { recFmtDT } from './EvalSections.js';
import { useAutosaveFlush } from '../../lib/unsaved.js';
import { BarCard, Gates, GradeGroups, Seg, V2Ic, YesNo, fmt1, type Gate } from './V2Ui.js';

const errMsg = (fallback: string) => (err: unknown) =>
  toast.danger((err as { body?: { message?: string } }).body?.message ?? fallback);
const fmtD = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');

// Préavis : délai obligatoire (liste), date exacte optionnelle.
export const REC_DISPO_DELAYS: Array<[RecDispoDelay, string]> = [
  ['now', 'Disponible immédiatement'], ['1m', '1 mois'], ['2m', '2 mois'], ['3m', '3 mois'],
];
export const recDispoLbl = (d: RecDispoDelay | null | undefined) => REC_DISPO_DELAYS.find(([k]) => k === d)?.[1] ?? null;
// Date estimée = aujourd'hui + délai (sert au contrôle « disponibilité vs date cible » sans date exacte).
const delayDate = (d: RecDispoDelay) => {
  const t = new Date(); t.setMonth(t.getMonth() + (d === 'now' ? 0 : Number(d[0])));
  return new Date(t.getTime() - t.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};
const GENERAL = '_general';

export function useRecSt() {
  return useRecSettings().data ?? REC_SETTINGS_DEFAULT;
}

// ─── appel : stepper du script ──────────────────────────────
interface ScriptStep { id: string; t?: string; say?: string; q?: string; follow?: string[]; ph?: string }

export function CallSectionV2({ app, req, role, canEdit, canSal, meName, onStop }: {
  app: RecApplicationDTO; req: RecRequisitionDTO; role: RecRoleDTO | undefined;
  canEdit: boolean; canSal: boolean; meName: string; onStop: () => void;
}) {
  const save = useRecCallSave();
  const close = useRecCallClose();
  const plan = useRecCallPlan();
  const report = useRecCallReport();
  const me = useMe();
  const c = app.call;
  const interviewers = useRecInterviewers(canEdit && app.stage === 'APPEL' && !c?.completedAt);
  // datetime-local attend l'heure locale, pas l'ISO UTC.
  const localDt = (iso: string | null | undefined) => {
    if (!iso) return '';
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  };
  const [planDt, setPlanDt] = useState(localDt(c?.scheduledAt));
  const [planWho, setPlanWho] = useState(c?.callerId ?? '');
  useEffect(() => { setPlanDt(localDt(app.call?.scheduledAt)); setPlanWho(app.call?.callerId ?? ''); }, [app.id, app.call?.scheduledAt, app.call?.callerId]); // eslint-disable-line react-hooks/exhaustive-deps
  const script = ((role?.templates.script ?? []) as unknown as ScriptStep[]);
  const v2 = script.every((s) => !!s.t);
  const done = !!c?.completedAt;
  const editable = canEdit && !done && app.stage === 'APPEL';
  const [step, setStep] = useState(done ? script.length - 1 : Math.min(1, script.length - 1));
  const [notes, setNotes] = useState<Record<string, string>>(c?.answers ?? {});
  const [f, setF] = useState({
    pretAmount: c?.pretAmount != null ? String(c.pretAmount) : '',
    dispoDate: c?.dispoDate ?? '', dispoDelay: (c?.dispoDelay ?? '') as RecDispoDelay | '', commune: c?.commune ?? '', vehicule: c?.vehicule ?? null,
    trajetMin: c?.trajetMin != null ? String(c.trajetMin) : '', chkTrajet: c?.chkTrajet ?? null, chkPoste: c?.chkPoste ?? null,
  });
  useEffect(() => { setNotes(app.call?.answers ?? {}); }, [app.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Les notes partent au blur ; sans blur (raccourci clavier, fermeture
  // du panneau) elles partent quand même au moment de quitter.
  const notesPending = !!c && editable && JSON.stringify(notes) !== JSON.stringify(c.answers ?? {});
  useAutosaveFlush(`rec-call-${app.id}`, () => {
    if (notesPending) save.mutate({ id: app.id, answers: notes }, { onError: errMsg('Réponses non enregistrées.') });
  });
  if (!c) return <div className="rec-lockbox"><span className="big">📞</span>Pas encore d’appel — il s’arme au passage à l’étape « Appel de qualif ».</div>;
  if (!script.length) return <div className="rec-emptybox">Ce rôle n’a pas de script d’appel au catalogue.</div>;

  // Prétentions toujours en net ; une ancienne saisie en brut reste brute tant qu'on n'y touche pas.
  const pretTypeFor = (amt: string): 'net' | 'brut' => (c.pretType === 'brut' && amt === String(c.pretAmount ?? '') ? 'brut' : 'net');
  const payload = () => ({
    answers: notes,
    ...(f.pretAmount !== '' ? { pretAmount: Number(f.pretAmount), pretType: pretTypeFor(f.pretAmount) } : {}),
    dispoDate: f.dispoDate || null, ...(f.dispoDelay ? { dispoDelay: f.dispoDelay } : {}), commune: f.commune || null,
    vehicule: f.vehicule, trajetMin: f.trajetMin !== '' ? Number(f.trajetMin) : null,
    chkTrajet: f.chkTrajet, chkPoste: f.chkPoste,
  });
  const persist = (patch?: Partial<typeof f>) => {
    const next = { ...f, ...(patch ?? {}) };
    if (patch) setF(next);
    if (!editable) return;
    save.mutate({ id: app.id, ...{ ...payload(), ...(patch ? {
      ...(next.pretAmount !== '' ? { pretAmount: Number(next.pretAmount), pretType: pretTypeFor(next.pretAmount) } : {}),
      dispoDate: next.dispoDate || null, ...(next.dispoDelay ? { dispoDelay: next.dispoDelay } : {}), commune: next.commune || null,
      vehicule: next.vehicule, trajetMin: next.trajetMin !== '' ? Number(next.trajetMin) : null,
      chkTrajet: next.chkTrajet, chkPoste: next.chkPoste,
    } : {}) } }, { onError: errMsg('Réponses non enregistrées.') });
  };
  const band = req.band;
  const amount = f.pretAmount !== '' ? Number(f.pretAmount) : null;
  const brut = amount == null ? null : pretTypeFor(f.pretAmount) === 'brut' ? amount : Math.round(amount / 0.8 / 1000) * 1000;
  const inBand = canSal ? (brut != null && band ? brut <= band[1] : null) : c.pretInBand;
  const sal = (canSal ? amount == null : !c.pretSet && amount == null)
    ? { st: '', ic: '…', tx: 'Prétentions à saisir (étape « Prétentions »)' }
    : inBand == null ? { st: 'ok', ic: '✓', tx: 'Pas de fourchette définie sur ce poste' }
      : inBand ? { st: 'ok', ic: '✓', tx: canSal && brut != null && band ? `${recFmtMoney(brut)} brut — dans la fourchette ${recFmtMoney(band[0])} – ${recFmtMoney(band[1])}` : 'Dans la fourchette du rôle — montant visible de l’owner' }
        : { st: 'warn', ic: '!', tx: canSal && brut != null && band ? `${recFmtMoney(brut)} brut — ${recFmtMoney(brut - band[1])} au-dessus du haut de fourchette` : 'Au-dessus de la fourchette du rôle — montant visible de l’owner' };
  const dispoAt = f.dispoDate || (f.dispoDelay ? delayDate(f.dispoDelay) : '');
  const dd = dispoAt ? Math.round((new Date(`${dispoAt}T12:00:00`).getTime() - new Date(`${req.targetStart}T12:00:00`).getTime()) / 864e5) : null;
  const dispoWhen = f.dispoDate ? `Disponible le ${fmtD(f.dispoDate)}` : f.dispoDelay === 'now' ? 'Disponible immédiatement' : f.dispoDelay ? `Disponible sous ${recDispoLbl(f.dispoDelay)} (≈ ${fmtD(dispoAt)})` : '';
  const dispo = !f.dispoDelay ? { st: '', ic: '…', tx: 'Délai de disponibilité à choisir (étape « Préavis ») — obligatoire' }
    : dd! <= 0 ? { st: 'ok', ic: '✓', tx: `${dispoWhen} — avant la date cible du ${fmtD(req.targetStart)}` }
      : { st: 'warn', ic: '!', tx: `${dispoWhen} — ${dd} j après la date cible du ${fmtD(req.targetStart)}` };
  const tr = Number(f.trajetMin) || 0;
  const ready = f.chkTrajet != null && f.chkPoste != null && !!f.dispoDelay;
  const doClose = (verdict: 'go' | 'vivier' | 'stop') => close.mutate({ id: app.id, verdict, ...payload() }, {
    onSuccess: () => {
      if (verdict === 'go') toast.success(`${app.candidate.firstName} — appel clôturé, passage au cas pratique. Préparez le brief.`);
      else if (verdict === 'vivier') toast.success(`${app.candidate.firstName} — ajouté·e au vivier.`);
      else onStop();
    },
    onError: errMsg('Clôture impossible.'),
  });
  // « Appel terminé » : l'appelant prévient l'owner, qui vérifie puis
  // tranche ; rien ne bouge dans le pipeline d'ici là.
  const reported = !!c.reportedAt && !done;
  const canUndo = editable && (c.reportedBy === me.data?.user.name || (me.data?.permissions ?? []).includes('recrutement.offre'));
  const missing = [
    !f.dispoDelay && 'délai de disponibilité',
    amount == null && !c.pretSet && 'prétentions',
    (f.chkTrajet == null || f.chkPoste == null) && 'contrôles trajet / poste',
  ].filter(Boolean) as string[];
  const doReport = async (undo = false) => {
    try {
      if (!undo) await save.mutateAsync({ id: app.id, ...payload() });
      await report.mutateAsync({ id: app.id, ...(undo ? { undo: true } : {}) });
      toast.success(undo ? 'Signalement annulé — l’appel reste en cours.' : 'Appel signalé terminé — l’owner est prévenu pour vérifier.');
    } catch (err) { errMsg(undo ? 'Annulation impossible.' : 'Signalement impossible.')(err); }
  };
  const S = script[step]!;
  const isLast = step === script.length - 1;
  const kind = S.id === 'preavis' ? 'dispo' : S.id === 'lieu' ? 'lieu' : S.id === 'pret' ? 'pret' : S.say && !S.q ? 'say' : 'notes';
  const filled = (s: ScriptStep, i: number) => (s.id === 'preavis' ? !!f.dispoDelay : s.id === 'lieu' ? !!f.commune && f.vehicule != null : s.id === 'pret' ? amount != null || c.pretSet : s.say && !s.q ? i < step || done : !!(notes[s.id] ?? '').trim());

  return (
    <div>
      <div className="rec-rule">
        <span className="ic">📞</span>
        <span>{done
          ? <>Appel réalisé par <b>{c.caller ?? '—'}</b> — verdict <b>{c.verdict === 'go' ? 'avancer → cas pratique' : c.verdict}</b>.</>
          : <>{c.scheduledAt
            ? <>Appel planifié le <b>{recFmtDT(c.scheduledAt)}</b>{c.caller ? <> par <b>{c.caller}</b></> : ''}. </>
            : c.reportedAt ? null : <>Appel <b>à planifier</b>. </>}
            Script SHIFTIN v1.0 — 7 temps. Poste sur place à Birkhadem : <b>plus de question « hybride »</b>. Les réponses s’enregistrent en continu.</>}
        </span>
      </div>
      {reported && (
        <div className="rv2-callrep" data-testid="rec-call-reported">
          <span className="ic">✓</span>
          <div className="tx">
            <b>Appel terminé — à vérifier</b>
            <span>Signalé par {c.reportedBy ?? '—'} le {recFmtDT(c.reportedAt!)}. Relisez les notes puis tranchez au dernier temps (« avancer », vivier ou rejet).</span>
          </div>
          {canUndo && <button className="rec-ghost sm" disabled={report.isPending} onClick={() => void doReport(true)}>Annuler</button>}
        </div>
      )}
      {editable && !reported && (
        <div className="rec-callplan" data-testid="rec-callplan">
          <div className="rec-field">
            <label htmlFor={`cp-dt-${app.id}`}>Date &amp; heure</label>
            <input id={`cp-dt-${app.id}`} type="datetime-local" value={planDt} onChange={(e) => setPlanDt(e.target.value)} />
          </div>
          <div className="rec-field">
            <label htmlFor={`cp-who-${app.id}`}>Mené par</label>
            <select id={`cp-who-${app.id}`} value={planWho} onChange={(e) => setPlanWho(e.target.value)}>
              <option value="">— à définir —</option>
              {(interviewers.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <button
            className="rec-ghost sm" data-testid="rec-callplan-save"
            disabled={plan.isPending || (!planDt && !planWho)
              || (planDt === localDt(c.scheduledAt) && planWho === (c.callerId ?? ''))}
            onClick={() => plan.mutate(
              { id: app.id, scheduledAt: planDt ? new Date(planDt).toISOString() : null, callerId: planWho || null },
              { onSuccess: () => toast.success('Appel planifié.'), onError: errMsg('Planification impossible.') },
            )}
          >
            {c.scheduledAt ? 'Replanifier' : 'Planifier'}
          </button>
        </div>
      )}
      {!v2 && <div className="rv2-ovwarn" style={{ marginBottom: 12 }}>Ce rôle a encore un script v1 — mettez-le à jour dans le catalogue (étapes SHIFTIN v1.0).</div>}
      <div className="rv2-call" style={{ '--c4': '#2a6fdb' } as React.CSSProperties}>
        <div className="rv2-steps">
          {script.map((s, i) => (
            <button key={s.id} className={`rv2-step${step === i ? ' on' : ''}${filled(s, i) ? ' done' : ''}`} onClick={() => setStep(i)}>
              <span className="k">{filled(s, i) && step !== i ? '✓' : i}</span><b>{s.t ?? s.q}</b>
            </button>
          ))}
        </div>
        <div className="rv2-scard" key={step}>
          <div className="hd"><span className="k">{String(step).padStart(2, '0')}</span><h4>{S.t ?? 'Question'}</h4></div>
          {S.say && <div className="rv2-say">{S.say.replace('{{prenom}}', app.candidate.firstName).replace('{{moi}}', meName.split(' ')[0] ?? '').replace('{{entite}}', 'SHIFTIN')}</div>}
          {S.q && <p className="rv2-q">{S.q}</p>}
          {!!S.follow?.length && (
            <div className="rv2-follow">
              <span className="rec-lbl" style={{ margin: '0 0 2px' }}>Relances · {role?.label}</span>
              {S.follow.map((x) => <div key={x}>{x}</div>)}
            </div>
          )}
          {kind === 'notes' && S.q && (
            <textarea className="rec-ta" rows={3} disabled={!editable} placeholder="Notes de l’appel — des faits, pas des impressions"
              value={notes[S.id] ?? ''} onChange={(e) => setNotes({ ...notes, [S.id]: e.target.value })} onBlur={() => persist()}></textarea>
          )}
          {kind === 'dispo' && (
            <div className="rv2-fields">
              <div className="rec-field" style={{ margin: 0 }}>
                <label>Disponibilité <span className="rv2-req">obligatoire</span></label>
                <select disabled={!editable} value={f.dispoDelay} onChange={(e) => persist({ dispoDelay: e.target.value as RecDispoDelay })} data-testid="rec-dispo-delay">
                  {!f.dispoDelay && <option value="">— choisir —</option>}
                  {REC_DISPO_DELAYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div className="rec-field" style={{ margin: 0 }}><label>Date exacte (optionnelle)</label><input type="date" disabled={!editable} value={f.dispoDate} onChange={(e) => persist({ dispoDate: e.target.value })} /></div>
              <div className="rec-field" style={{ margin: 0 }}><label>Date cible du poste</label><input disabled value={fmtD(req.targetStart)} /></div>
            </div>
          )}
          {kind === 'lieu' && (
            <div className="rv2-fields">
              <div className="rec-field" style={{ margin: 0 }}><label>Commune</label><input disabled={!editable} value={f.commune} onChange={(e) => setF({ ...f, commune: e.target.value })} onBlur={() => persist()} placeholder="Ex. Bab Ezzouar" /></div>
              <div className="rec-field" style={{ margin: 0 }}><label>Véhiculé·e</label><div><YesNo v={f.vehicule} disabled={!editable} onSet={(v) => persist({ vehicule: v })} /></div></div>
              <div className="rec-field" style={{ margin: 0 }}><label>Temps de trajet (min)</label><input type="number" disabled={!editable} value={f.trajetMin} onChange={(e) => setF({ ...f, trajetMin: e.target.value })} onBlur={() => persist()} /></div>
            </div>
          )}
          {kind === 'pret' && (canSal || editable ? (
            <div className="rv2-fields">
              <div className="rec-field" style={{ margin: 0 }}><label>Montant mensuel net (DZD)</label><input type="number" disabled={!editable} value={f.pretAmount} placeholder={c.pretSet && !canSal ? 'déjà saisi — masqué' : '130000'} onChange={(e) => setF({ ...f, pretAmount: e.target.value })} onBlur={() => persist()} /></div>
              {!canSal && <div className="rec-hint" style={{ gridColumn: '1/-1' }}>🔒 Saisie à l’aveugle — le montant n’est visible que de l’owner ; vous voyez seulement s’il est dans la fourchette.</div>}
            </div>
          ) : <span className="rec-lockpill">🔒 Prétentions — owner</span>)}
          {isLast && (
            <>
              <div className="rec-lbl" style={{ margin: '14px 0 8px' }}>4 contrôles avant le verdict</div>
              <div className="rv2-checks">
                <div className={`rv2-check ${sal.st}`}><span className="ci">{sal.ic}</span><div className="tx"><b>Salaire vs fourchette <span className="rv2-auto">auto</span></b><span>{sal.tx}</span></div></div>
                <div className={`rv2-check ${dispo.st}`}><span className="ci">{dispo.ic}</span><div className="tx"><b>Disponibilité vs date cible <span className="rv2-auto">auto</span></b><span>{dispo.tx}</span></div></div>
                <div className={`rv2-check ${f.chkTrajet === true ? 'ok' : f.chkTrajet === false ? 'ko' : ''}`}><span className="ci">{f.chkTrajet === true ? '✓' : f.chkTrajet === false ? '✕' : '?'}</span>
                  <div className="tx"><b>Trajet faisable</b><span>{f.commune || 'commune ?'} → Birkhadem · {f.vehicule ? 'véhiculé·e' : f.vehicule === false ? 'transports' : 'mode ?'} · {tr || '?'} min{tr > 60 && !f.vehicule ? ' — long sans véhicule' : ''}</span>
                    <div style={{ marginTop: 7 }}><YesNo v={f.chkTrajet} disabled={!editable} onSet={(v) => persist({ chkTrajet: v })} /></div></div></div>
                <div className={`rv2-check ${f.chkPoste === true ? 'ok' : f.chkPoste === false ? 'ko' : ''}`}><span className="ci">{f.chkPoste === true ? '✓' : f.chkPoste === false ? '✕' : '?'}</span>
                  <div className="tx"><b>Le poste actuel correspond au rôle</b><span>D’après « Poste actuel au quotidien ».</span>
                    <div style={{ marginTop: 7 }}><YesNo v={f.chkPoste} disabled={!editable} onSet={(v) => persist({ chkPoste: v })} /></div></div></div>
              </div>
              {editable && (
                <>
                  <div className="rec-verdict">
                    <button className="go" disabled={!ready || close.isPending} onClick={() => doClose('go')}>Avancer → Cas pratique</button>
                    <button className="mid" disabled={!ready || close.isPending} onClick={() => doClose('vivier')}>Vivier</button>
                    <button className="stop" disabled={!ready || close.isPending} onClick={() => doClose('stop')}>Rejeter</button>
                  </div>
                  {!ready && <div className="rec-hint" style={{ marginTop: 8 }}>{f.dispoDelay ? 'Répondez aux deux contrôles manuels pour clôturer l’appel.' : 'Choisissez le délai de disponibilité (étape « Préavis ») et répondez aux deux contrôles manuels pour clôturer l’appel.'}</div>}
                </>
              )}
            </>
          )}
          <div className="rv2-nav">
            <button className="rec-ghost sm" disabled={step === 0} onClick={() => setStep(step - 1)}>← Précédent</button>
            {!isLast && <button className="rec-go sm" onClick={() => setStep(step + 1)}>Suivant → {script[step + 1]!.t ?? ''}</button>}
          </div>
        </div>
      </div>
      <div className="rv2-gnotes" data-testid="rec-call-general">
        <div className="rec-lbl" style={{ margin: '0 0 6px' }}>Notes générales de l’appel</div>
        <textarea className="rec-ta" rows={3} disabled={!editable} placeholder="Impression d’ensemble, points hors script, à vérifier plus tard…"
          value={notes[GENERAL] ?? ''} onChange={(e) => setNotes({ ...notes, [GENERAL]: e.target.value })} onBlur={() => persist()}></textarea>
      </div>
      {editable && !reported && (
        <div className="rv2-callend">
          <div className="tx">
            <b>Fini avec ce candidat ?</b>
            <span>{missing.length ? `Encore vide : ${missing.join(', ')}. ` : ''}Signalez l’appel terminé : l’owner est notifié pour vérifier. Le candidat reste à l’étape Appel jusqu’au verdict.</span>
          </div>
          <button className="rec-go sm" data-testid="rec-call-report" disabled={report.isPending || save.isPending} onClick={() => void doReport()}>✓ Appel terminé</button>
        </div>
      )}
    </div>
  );
}

// ─── cas pratique ───────────────────────────────────────────
export function TestSection({ app, role, canEdit, canSal, onReject }: {
  app: RecApplicationDTO; role: RecRoleDTO | undefined; canEdit: boolean; canSal: boolean; onReject: () => void;
}) {
  const st = useRecSt();
  const patch = useRecTestPatch();
  const send = useRecTestSend();
  const submit = useRecTestSubmit();
  const pay = useRecTestPay();
  const score = useRecTestScore();
  const advance = useRecTestAdvance();
  const t = app.test;
  const [link, setLink] = useState('');
  const [draft, setDraft] = useState<{ sc: Record<string, number>; ev: Record<string, string> }>({ sc: {}, ev: {} });
  useEffect(() => { setDraft({ sc: {}, ev: {} }); setLink(''); }, [app.id]);
  if (!t) {
    return (
      <div className="rec-lockbox">
        <span className="big"><V2Ic k="TEST" s={30} w={1.4} /></span>
        <h4>Cas pratique rémunéré</h4>
        Il s’ouvre quand l’appel est clôturé avec le verdict « avancer » : brief, livrables et rubrique viennent du template du rôle {role ? `« ${role.label} »` : ''}.
      </div>
    );
  }
  const rub = t.rubric as RecGridCrit[];
  const order = ['A_ENVOYER', 'ENVOYE', 'RENDU', 'NOTE'];
  const cur = order.indexOf(t.status);
  const graded = t.status === 'NOTE';
  const steps = [
    { lbl: 'Brief', ic: 'doc', sub: t.sentAt ? fmtD(t.sentAt).slice(0, 5) : 'à transmettre' },
    { lbl: 'En cours', ic: 'clock', sub: t.dueAt ? `dû ${fmtD(t.dueAt).slice(0, 5)} ${new Date(t.dueAt).toTimeString().slice(0, 5)}` : `${t.delayH} h` },
    { lbl: 'Rendu', ic: 'inbox', sub: t.submittedAt ? fmtD(t.submittedAt).slice(0, 5) : '—' },
    { lbl: 'Noté', ic: 'star', sub: graded ? `${fmt1(t.score)}/5` : '—' },
  ];
  const hoursLeft = t.dueAt ? Math.round((new Date(t.dueAt).getTime() - Date.now()) / 36e5) : null;
  const draftS = recDimScore(rub, draft.sc);
  const evMiss = rub.filter((g) => recNeedsEvidence(draft.sc[g.id]) && !(draft.ev[g.id] ?? '').trim()).length;
  const pass = graded && t.score != null && t.score >= st.testPass;
  const scorable = t.status === 'RENDU' && canEdit;
  return (
    <div style={{ '--c4': '#0bb5ab' } as React.CSSProperties}>
      <div className="rv2-tl">
        {steps.map((s, i) => (
          <div key={s.lbl} className={`rv2-tln${i < cur || (graded && i === cur) ? ' done' : i === cur ? ' now' : ''}`}>
            <span className="d"><V2Ic k={i < cur || (graded && i === cur) ? 'check' : s.ic} s={16} w={2} /></span>
            <b>{s.lbl}</b><span>{s.sub}</span>
          </div>
        ))}
      </div>
      <div className="rv2-vgrid wide-l">
        <div className="rv2-brief">
          <span className="rec-lbl" style={{ margin: 0 }}>Brief — copie figée du template « Cas pratique »</span>
          <p>{t.brief || '—'}</p>
          <div className="rv2-deliv">{t.deliverables.map((d) => <span key={d}>📎 {d}</span>)}</div>
        </div>
        <div className="r4-card" style={{ margin: 0 }}>
          <h4 style={{ color: 'var(--ink3)' }}><V2Ic k="clock" s={14} w={2} /> Délai & indemnité</h4>
          {t.status === 'A_ENVOYER' && canEdit && (
            <div style={{ marginBottom: 10 }}><Seg v={String(t.delayH) as '48' | '72'} opts={[['48', '48 h'], ['72', '72 h']]} onSet={(v) => patch.mutate({ id: app.id, delayH: Number(v) }, { onError: errMsg('Délai non enregistré.') })} /></div>
          )}
          {t.status === 'ENVOYE' && hoursLeft != null && (
            <div className="rv2-count"><b>{Math.max(hoursLeft, 0)}</b><i>h restantes · à rendre le {fmtD(t.dueAt!)}</i></div>
          )}
          <div className="rec-meta" style={{ marginTop: 6 }}>
            <div className="r"><span>Délai</span><b>{t.delayH} h</b></div>
            <div className="r"><span>Indemnité</span>{canSal && t.fee != null ? <b className="rec-sal">{recFmtMoney(t.fee)}</b> : <span className="rec-lockpill">🔒 Confidentiel</span>}</div>
            <div className="r"><span>Payée</span><b>{t.paidAt ? `✓ le ${fmtD(t.paidAt)}` : t.status === 'RENDU' || graded ? 'à payer' : 'après le rendu'}</b></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {t.status === 'A_ENVOYER' && canEdit && (
              <button className="rec-go sm" disabled={send.isPending} onClick={() => send.mutate({ id: app.id }, { onSuccess: () => toast.success('Marqué envoyé — échéance armée. Rien n’est parti au candidat : transmettez le brief vous-même.'), onError: errMsg('Envoi impossible.') })}>Marquer envoyé</button>
            )}
            {(t.status === 'RENDU' || graded) && !t.paidAt && canSal && (
              <button className="rec-ghost sm" disabled={pay.isPending} onClick={() => pay.mutate({ id: app.id }, { onSuccess: () => toast.success('Indemnité marquée payée.'), onError: errMsg('Paiement non enregistré.') })}>Marquer l’indemnité payée</button>
            )}
          </div>
        </div>
      </div>
      {t.status === 'ENVOYE' && canEdit && (
        <div className="rec-callplan" style={{ marginTop: 14 }}>
          <div className="rec-field"><label>Lien du rendu</label><input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Drive, WeTransfer, GitHub…" /></div>
          <button className="rec-go sm" disabled={link.trim().length < 3 || submit.isPending} onClick={() => submit.mutate({ id: app.id, link: link.trim() }, { onSuccess: () => toast.success('Rendu enregistré — notification au manager recruteur.'), onError: errMsg('Rendu non enregistré.') })}>Marquer rendu</button>
        </div>
      )}
      {(t.status === 'RENDU' || graded) && (
        <div style={{ marginTop: 16 }}>
          <div className="rec-blockh" style={{ marginBottom: 10 }}>
            <span className="n">Rubrique</span>
            <h4>{graded ? `Noté par ${t.scorer ?? '—'}` : 'Notez le rendu'}</h4>
            {t.link && <a className="rec-ghost sm" href={/^https?:/.test(t.link) ? t.link : `https://${t.link}`} target="_blank" rel="noreferrer" style={{ marginLeft: 8 }}>Ouvrir le rendu ↗</a>}
            <span className="avg">pondérée <b>{fmt1(graded ? t.score : draftS.overall)}</b>/5</span>
          </div>
          <GradeGroups grid={rub} readOnly={!scorable} scores={graded ? t.scores : draft.sc} evidence={graded ? t.evidence : draft.ev}
            onScore={(id, v) => setDraft((d) => ({ ...d, sc: { ...d.sc, [id]: v } }))}
            onEv={(id, v) => setDraft((d) => ({ ...d, ev: { ...d.ev, [id]: v } }))} />
          {scorable && (
            <div className="rec-formfoot">
              <div><div className="gn">{fmt1(draftS.overall)}<i>/5</i></div><div className="lb">note du cas</div></div>
              <div className="rec-prog"><i style={{ width: `${(Object.keys(draft.sc).length / Math.max(rub.length, 1)) * 100}%` }}></i></div>
              <button className="rec-go" disabled={!draftS.complete || evMiss > 0 || score.isPending} onClick={() => score.mutate({ id: app.id, scores: draft.sc, evidence: draft.ev }, { onSuccess: () => toast.success('Cas pratique noté.'), onError: errMsg('Note non enregistrée.') })}>Enregistrer la note</button>
            </div>
          )}
          {scorable && evMiss > 0 && <div className="rec-hint" style={{ marginTop: 6, color: 'var(--warnc)' }}>{evMiss} preuve{evMiss > 1 ? 's' : ''} manquante{evMiss > 1 ? 's' : ''} — obligatoire pour une note de 1, 2 ou 5.</div>}
          {graded && (pass ? (
            <div className="rv2-outcome ok">
              <span className="big">{fmt1(t.score)}</span>
              <div className="tx"><b>Cas réussi — seuil {fmt1(st.testPass)}</b>Prochaine étape : un entretien « Défense du cas pratique ».</div>
              {app.stage === 'TEST' && canEdit && <button className="rec-go" disabled={advance.isPending} onClick={() => advance.mutate({ id: app.id }, { onSuccess: () => toast.success(`${app.candidate.firstName} passe en entretien — planifiez la « Défense du cas pratique ».`), onError: errMsg('Passage impossible.') })}>Avancer → Entretien</button>}
            </div>
          ) : (
            <div className="rv2-outcome ko">
              <span className="big">{fmt1(t.score)}</span>
              <div className="tx"><b>Sous {fmt1(st.testPass)} — la candidature ne peut pas avancer</b>Seul le rejet est possible, motif « Échec cas pratique ».</div>
              {app.stage === 'TEST' && canEdit && <button className="rec-go" style={{ background: 'var(--bad)' }} onClick={onReject}>Rejeter…</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── vérification ───────────────────────────────────────────
export function VerifSection({ app, canOffre, dark, onForceReject }: {
  app: RecApplicationDTO; canOffre: boolean; dark: boolean; onForceReject: () => void;
}) {
  const patch = useRecVerifPatch();
  const v = app.verification;
  const vg = app.gates.verif;
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [notes, setNotes] = useState({ certNote: v?.certNote ?? '', portNote: v?.portNote ?? '' });
  const avail = ['ENTRETIEN', 'FINALISTE', 'OFFRE', 'RECRUTE'].includes(app.stage);
  if (!avail && !v) {
    return <div className="rec-lockbox"><span className="big"><V2Ic k="VERIF" s={30} w={1.4} /></span><h4>Vérification</h4>Disponible dès l’étape Entretien. Elle conditionne l’offre : on vérifie avant, jamais après.</div>;
  }
  const editable = canOffre && avail && app.stage !== 'RECRUTE';
  const set = (p: Record<string, unknown>) => patch.mutate({ id: app.id, ...p }, { onError: errMsg('Vérification non enregistrée.') });
  const C = 2 * Math.PI * 19;
  const refs = (v?.refs ?? []) as RecRef[];
  return (
    <div>
      {!canOffre && <div className="rv2-ovwarn" style={{ marginBottom: 12, background: 'var(--infos)', color: 'var(--infoc)' }}>La vérification est menée par l’owner — vous voyez où elle en est.</div>}
      <div className="rv2-vring">
        <span className="cat-ring" style={{ '--rc': vg.ok ? 'var(--green)' : 'var(--warn)', width: 48, height: 48, flex: '0 0 48px' } as React.CSSProperties}>
          <svg width="48" height="48"><circle className="bgc" cx="24" cy="24" r="19"></circle><circle className="fgc" cx="24" cy="24" r="19" strokeDasharray={C} strokeDashoffset={C * (1 - vg.done / 3)}></circle></svg>
          <b>{vg.done}/3</b>
        </span>
        <div style={{ flex: 1, minWidth: 220 }}>
          <b style={{ fontSize: 13.5, fontWeight: 800 }}>{vg.ok ? 'Vérification complète — la porte de l’offre est ouverte' : vg.problem ? 'Incohérence détectée — rejet obligatoire' : 'Vérification en cours — l’offre reste verrouillée'}</b>
          <div className="rec-hint" style={{ marginTop: 3 }}>Exigé : ≥ 2 références dont ≥ 1 manager · certificats de travail vérifiés · intégrité OK.</div>
        </div>
        <span className={`rec-stpill ${vg.refsOk ? 'green' : 'warn'}`}>{vg.n} réf. · {vg.mgr} manager</span>
        <span className={`rec-stpill ${vg.cert ? 'green' : 'warn'}`}>certificats {vg.cert ? '✓' : '…'}</span>
        <span className={`rec-stpill ${vg.integ ? 'green' : vg.problem ? 'bad' : 'warn'}`}>intégrité {vg.integ ? 'OK' : vg.problem ? '✕' : '…'}</span>
      </div>
      <div className="rv2-vgrid">
        <div style={{ display: 'grid', gap: 14 }}>
          <div className="rv2-vcard">
            <h5>Documents</h5>
            <div className="rv2-vrow">
              <div className="nm">Certificats de travail conformes au CV<span>dates, intitulés, employeurs</span>
                <input disabled={!editable} value={notes.certNote} onChange={(e) => setNotes({ ...notes, certNote: e.target.value })} onBlur={() => editable && set({ certNote: notes.certNote })} placeholder="Note de vérification" /></div>
              <YesNo v={v?.cert} disabled={!editable} onSet={(x) => set({ cert: x })} />
            </div>
            <div className="rv2-vrow"><div className="nm">Diplômes vus<span>original ou copie légalisée</span></div><YesNo v={v?.dipl} disabled={!editable} onSet={(x) => set({ dipl: x })} /></div>
            <div className="rv2-vrow">
              <div className="nm">Paternité du portfolio confirmée<span>projets cités confirmés par une référence</span>
                <input disabled={!editable} value={notes.portNote} onChange={(e) => setNotes({ ...notes, portNote: e.target.value })} onBlur={() => editable && set({ portNote: notes.portNote })} placeholder="Qui l’a confirmé, sur quels projets" /></div>
              <YesNo v={v?.port} disabled={!editable} onSet={(x) => set({ port: x })} />
            </div>
          </div>
          <div className="rv2-vcard">
            <h5>Intégrité</h5>
            <div className="rv2-integ">
              <button className={`ok${v?.integrity === 'OK' ? ' on' : ''}`} disabled={!editable} onClick={() => set({ integrity: 'OK' })}><b>✓ Intégrité OK</b><span>Rien d’incohérent entre CV, appel et références</span></button>
              <button className={`ko${v?.integrity === 'PROBLEME' ? ' on' : ''}`} disabled={!editable} onClick={() => set({ integrity: 'PROBLEME' })}><b>✕ Problème détecté</b><span>Mensonge, dates fausses, portfolio emprunté…</span></button>
            </div>
            {v?.integrity === 'PROBLEME' && (
              <div className="rec-outband" style={{ alignItems: 'center' }}>
                <span style={{ flex: 1 }}><b>Incohérence à la vérification.</b> Rejet obligatoire — aucune dérogation, même pour le BOSS.</span>
                {editable && app.stage !== 'REJETE' && <button className="rec-go sm" style={{ background: 'var(--bad)' }} onClick={onForceReject}>Rejeter…</button>}
              </div>
            )}
          </div>
        </div>
        <div className="rv2-vcard">
          <h5>Références <span className={`rec-stpill ${vg.refsOk ? 'green' : 'warn'}`} style={{ marginLeft: 'auto' }}>{vg.n}/2 min · {vg.mgr}/1 manager</span></h5>
          {refs.map((r) => (
            <div key={r.id} className="rv2-refc" style={{ '--rc': r.relation === 'MANAGER' ? 'var(--shf)' : r.relation === 'PAIR' ? 'var(--info)' : 'var(--ink3)' } as React.CSSProperties}>
              <div className="hd" onClick={() => setOpen(open === r.id ? null : r.id)}>
                <span className="av">{r.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('')}</span>
                <div style={{ minWidth: 0 }}><b>{r.name}</b><span>{r.company} · appelé·e par {r.calledBy.split(' ')[0]} le {fmtD(r.date)}</span></div>
                <span className={`rel ${r.relation}`}>{r.relation === 'MANAGER' ? 'Manager' : r.relation === 'PAIR' ? 'Pair' : 'Autre'}</span>
                <span className={`rv2-rehire ${r.rehire}`}>Réembaucherait : {r.rehire === 'OUI' ? 'oui' : r.rehire === 'NON' ? 'non' : 'hésite'}</span>
              </div>
              {open === r.id && <div className="qa">{REC_REF_QUESTIONS.map((q, i) => (i === 4 ? null : <div key={q}><b>{q}</b>{r.answers[i] || '—'}</div>))}</div>}
            </div>
          ))}
          {!refs.length && <div className="rec-emptybox">Aucune référence appelée — 2 minimum dont 1 manager.</div>}
          {editable && <button className="rec-addrow" style={{ marginTop: 6 }} onClick={() => setAdding(true)}>＋ Appeler une référence — 6 questions guidées</button>}
        </div>
      </div>
      {adding && <RefModal app={app} dark={dark} onClose={() => setAdding(false)} />}
    </div>
  );
}

function RefModal({ app, dark, onClose }: { app: RecApplicationDTO; dark: boolean; onClose: () => void }) {
  const add = useRecVerifRef();
  const [f, setF] = useState({ name: '', company: '', relation: 'MANAGER' as RecRef['relation'], date: new Date().toISOString().slice(0, 10), answers: ['', '', '', '', '', ''], rehire: null as RecRef['rehire'] | null });
  const ok = f.name.trim() && f.company.trim() && f.rehire && f.answers.slice(0, 4).every((a) => a.trim());
  return (
    <RecModalHost dark={dark}>
      <div className="rec-modal-ov" onClick={onClose}>
        <div className="rec-modal" style={{ width: 'min(680px,94vw)' }} onClick={(e) => e.stopPropagation()}>
          <div className="rec-modal-h"><h3>Référence — {app.candidate.firstName} {app.candidate.lastName}</h3><button className="rec-x" onClick={onClose} aria-label="Fermer">✕</button></div>
          <div className="rec-modal-b">
            <div className="rec-f2" style={{ marginTop: 0 }}>
              <div className="rec-field" style={{ marginTop: 0 }}><label>Nom</label><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div className="rec-field" style={{ marginTop: 0 }}><label>Société</label><input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></div>
            </div>
            <div className="rec-lbl">Relation avec le candidat</div>
            <div className="rec-entrow">
              {(['MANAGER', 'PAIR', 'AUTRE'] as const).map((r) => <button key={r} className={`rec-epill${f.relation === r ? ' on' : ''}`} onClick={() => setF({ ...f, relation: r })}>{r === 'MANAGER' ? 'Manager' : r === 'PAIR' ? 'Pair' : 'Autre'}</button>)}
            </div>
            {REC_REF_QUESTIONS.map((q, i) => (
              <div key={q} className="rec-field">
                <label style={{ textTransform: 'none', letterSpacing: 0, fontSize: 11.5, color: 'var(--ink2)' }}>{i + 1}. {q}{i < 4 ? ' *' : ''}</label>
                {i === 4 ? (
                  <div className="rec-verdict" style={{ marginTop: 0 }}>
                    {([['OUI', 'Oui', 'go'], ['HESITATION', 'Hésitation', 'mid'], ['NON', 'Non', 'stop']] as const).map(([k, l, c]) => <button key={k} className={`${c}${f.rehire === k ? ' on' : ''}`} onClick={() => setF({ ...f, rehire: k })}>{l}</button>)}
                  </div>
                ) : (
                  <textarea rows={2} value={f.answers[i]} onChange={(e) => { const a = [...f.answers]; a[i] = e.target.value; setF({ ...f, answers: a }); }}></textarea>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end', alignItems: 'center' }}>
              <span className="rec-hint" style={{ marginRight: 'auto' }}>Nom, société, les 4 premières réponses et « réembaucheriez-vous » sont requis.</span>
              <button className="rec-ghost" onClick={onClose}>Annuler</button>
              <button className="rec-go" disabled={!ok || add.isPending} onClick={() => add.mutate({ id: app.id, name: f.name.trim(), company: f.company.trim(), relation: f.relation, date: f.date, answers: f.answers, rehire: f.rehire }, {
                onSuccess: () => { toast.success(`Référence enregistrée — ${f.name}.`); onClose(); },
                onError: errMsg('Référence non enregistrée.'),
              })}>Enregistrer la référence</button>
            </div>
          </div>
        </div>
      </div>
    </RecModalHost>
  );
}

// ─── portes de l'offre ──────────────────────────────────────
export function OfferGatesSection({ app, req, isBoss, canOffre, dark, onVerif, onBuild }: {
  app: RecApplicationDTO; req: RecRequisitionDTO; isBoss: boolean; canOffre: boolean; dark: boolean;
  onVerif: () => void; onBuild: () => void;
}) {
  const st = useRecSt();
  const [ov, setOv] = useState(false);
  const g = app.gates;
  const barOk = g.bar?.state === 'ok';
  const gates: Gate[] = [
    {
      k: 'bar', lbl: 'Barre d’embauche', ok: barOk, ov: !barOk && g.overridden, rule: 'Dérogation BOSS',
      tx: g.masked ? 'Masquée tant que votre scorecard n’est pas soumise.' : barOk ? `Global ≥ ${fmt1(st.barOverall)}, Performance ≥ ${fmt1(st.barPerf)}, aucune dimension < ${fmt1(st.barMin)}`
        : g.overridden ? `Dérogation — « ${app.barOverride?.comment} »` : g.bar?.state === 'ko' ? `Sous la barre — ${g.bar.fails.join(' · ')}` : 'Scorecards du jury incomplètes.',
      action: g.bar?.state === 'ko' && !g.overridden && app.stage === 'FINALISTE'
        ? (isBoss ? <button className="rec-ghost sm" onClick={() => setOv(true)}>Déroger…</button> : <span className="rec-lockpill">BOSS uniquement</span>)
        : undefined,
    },
    { k: 'test', lbl: 'Cas pratique réussi', ok: g.test.pass, hard: true, rule: 'Sans dérogation', tx: g.test.pass ? `${fmt1(g.test.score)}/5 — seuil ${fmt1(st.testPass)}` : 'Pas de cas pratique réussi.' },
    {
      k: 'verif', lbl: 'Vérification', ok: g.verif.ok, hard: true, rule: 'Sans dérogation',
      tx: g.verif.ok ? `${g.verif.n} références dont ${g.verif.mgr} manager · certificats vérifiés · intégrité OK`
        : [!g.verif.refsOk && `références ${g.verif.n}/2 (${g.verif.mgr} manager)`, !g.verif.cert && 'certificats à vérifier', !g.verif.integ && (g.verif.problem ? 'intégrité : problème' : 'intégrité non renseignée')].filter(Boolean).join(' · '),
      action: !g.verif.ok ? <button className="rec-ghost sm" onClick={onVerif}>Compléter →</button> : undefined,
    },
    {
      k: 'veto', lbl: 'Veto du manager recruteur', ok: !g.veto, hard: true, rule: 'Sans dérogation',
      tx: g.veto ? `${req.hiringManager?.name ?? 'Le manager recruteur'} a voté NON — l’offre est bloquée.` : g.mgrVerdict ? `${req.hiringManager?.name ?? 'Manager'} : ${g.mgrVerdict === 'oui' ? 'oui' : 'oui avec réserve'}` : 'Pas de vote NON du manager recruteur.',
    },
  ];
  const open = gates.every((x) => x.ok || x.ov);
  return (
    <div>
      {!g.mgrInJury && <div className="rv2-ovwarn" style={{ marginBottom: 12 }}>⚠ Le manager recruteur ({req.hiringManager?.name ?? '—'}) n’a siégé dans aucun jury — non bloquant, mais son avis manque.</div>}
      {g.jury && !g.masked && app.interviews.some((i) => (i.scorecards?.length ?? 0) > 0) && (
        <div style={{ marginBottom: 14 }}><BarCard s={g.jury} bar={g.bar} st={st} title="Moyenne du jury" overridden={g.overridden ? app.barOverride?.comment : null} /></div>
      )}
      <Gates gates={gates} />
      {app.stage === 'FINALISTE' && (
        <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {canOffre
            ? <button className="rec-go" disabled={!open} onClick={onBuild}>{open ? 'Construire l’offre →' : '🔒 Construire l’offre'}</button>
            : <span className="rec-lockpill">🔒 Offre — construite par l’owner</span>}
          <span className="rec-hint">{open ? 'La barre, la vérification et l’éventuelle dérogation sont jointes à la validation.' : 'Le bouton se déverrouille quand les 4 portes sont ouvertes.'}</span>
        </div>
      )}
      {ov && <OverrideModal app={app} dark={dark} onClose={() => setOv(false)} />}
    </div>
  );
}

function OverrideModal({ app, dark, onClose }: { app: RecApplicationDTO; dark: boolean; onClose: () => void }) {
  const st = useRecSt();
  const override = useRecBarOverride();
  const [c, setC] = useState('');
  const g = app.gates;
  return (
    <RecModalHost dark={dark}>
      <div className="rec-modal-ov" onClick={onClose}>
        <div className="rec-modal" style={{ width: 'min(620px,94vw)' }} onClick={(e) => e.stopPropagation()}>
          <div className="rec-modal-h"><h3>Déroger à la barre — {app.candidate.firstName} {app.candidate.lastName}</h3><button className="rec-x" onClick={onClose} aria-label="Fermer">✕</button></div>
          <div className="rec-modal-b">
            {g.jury && <BarCard s={g.jury} bar={g.bar} st={st} title="Moyenne du jury" />}
            <div className="rec-field">
              <label>Pourquoi embaucher malgré tout ? — obligatoire, 20 caractères minimum · {c.trim().length}/20</label>
              <textarea rows={4} value={c} onChange={(e) => setC(e.target.value)} placeholder="Ex. : la Performance est prouvée par le cas pratique, l’entretien l’a sous-évaluée…"></textarea>
            </div>
            <div className="rv2-audit" style={{ marginTop: 12 }}>journalisé : rec.offer.bar_override · auteur · règles en échec · commentaire</div>
            <div className="rv2-ovwarn" style={{ marginTop: 10 }}>La dérogation ne couvre que la barre. Cas pratique, vérification et veto du manager restent sans dérogation.</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="rec-ghost" onClick={onClose}>Annuler</button>
              <button className="rec-go" disabled={c.trim().length < 20 || override.isPending} onClick={() => override.mutate({ id: app.id, comment: c.trim() }, {
                onSuccess: () => { toast.success('Dérogation enregistrée — visible des validateurs et au journal.'); onClose(); },
                onError: errMsg('Dérogation impossible.'),
              })}>Confirmer la dérogation</button>
            </div>
          </div>
        </div>
      </div>
    </RecModalHost>
  );
}
